import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './accounting.css';

const ViewVoucher = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [voucher, setVoucher] = useState(null);

  useEffect(() => {
    fetchVoucher();
  }, [id]);

  const fetchVoucher = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/vouchers/${id}`);
      if (response.data.status) {
        setVoucher(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching voucher:', error);
      alert('حدث خطأ أثناء تحميل بيانات السند');
      navigate('/vouchers');
    } finally {
      setLoading(false);
    }
  };

  const handlePost = async () => {
    if (window.confirm('هل أنت متأكد من ترحيل هذا السند؟')) {
      try {
        const response = await axios.post(`/api/vouchers/${id}/post`);
        if (response.data.status) {
          alert('تم ترحيل السند بنجاح');
          fetchVoucher(); // Refresh data
        }
      } catch (error) {
        console.error('Error posting voucher:', error);
        alert('حدث خطأ أثناء ترحيل السند');
      }
    }
  };

  const handleCancel = async () => {
    if (window.confirm('هل أنت متأكد من إلغاء هذا السند؟')) {
      try {
        const response = await axios.post(`/api/vouchers/${id}/cancel`);
        if (response.data.status) {
          alert('تم إلغاء السند بنجاح');
          fetchVoucher(); // Refresh data
        }
      } catch (error) {
        console.error('Error cancelling voucher:', error);
        alert('حدث خطأ أثناء إلغاء السند');
      }
    }
  };

  const handleDelete = async () => {
    if (window.confirm('هل أنت متأكد من حذف هذا السند؟')) {
      try {
        const response = await axios.delete(`/api/vouchers/${id}`);
        if (response.data.status) {
          alert('تم حذف السند بنجاح');
          navigate('/vouchers');
        }
      } catch (error) {
        console.error('Error deleting voucher:', error);
        alert('حدث خطأ أثناء حذف السند');
      }
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const handleBack = () => {
    navigate('/vouchers');
  };

  const handleEdit = () => {
    navigate(`/vouchers/edit/${id}`);
  };

  if (loading) {
    return (
      <div className="vouchers-container">
        <div className="loading-container">
          <div className="loading-spinner">جاري التحميل...</div>
        </div>
      </div>
    );
  }

  if (!voucher) {
    return (
      <div className="vouchers-container">
        <div className="error-container">
          <h2>السند غير موجود</h2>
          <button onClick={handleBack} className="btn btn-primary">
            العودة إلى السندات
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="vouchers-container">
      <div className="page-header">
        <div className="page-title">
          <h3>{t('accounting.vouchers.viewVoucher')}</h3>
          <p>عرض تفاصيل السند رقم {voucher.voucher_number}</p>
        </div>
        <div className="header-actions">
          <button 
            type="button" 
            className="back-btn"
            onClick={handleBack}
          >
            ← {t('accounting.vouchers.actions.back')}
          </button>
          {voucher.status === 'draft' && (
            <button 
              type="button" 
              className="btn-edit"
              onClick={handleEdit}
            >
              {t('accounting.vouchers.actions.edit')}
            </button>
          )}
          {voucher.status === 'draft' && (
            <button 
              type="button" 
              className="btn-post"
              onClick={handlePost}
            >
              {t('accounting.vouchers.actions.post')}
            </button>
          )}
          {voucher.status === 'posted' && (
            <button 
              type="button" 
              className="btn-cancel"
              onClick={handleCancel}
            >
              {t('accounting.vouchers.actions.cancel')}
            </button>
          )}
          {voucher.status === 'draft' && (
            <button 
              type="button" 
              className="btn-delete"
              onClick={handleDelete}
            >
              {t('accounting.vouchers.actions.delete')}
            </button>
          )}
          <button 
            type="button" 
            className="btn-print"
            onClick={handlePrint}
          >
            {t('accounting.vouchers.actions.print')}
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="help-panels">
          <div className="help-panel">
            <div className="panel-header">
              <span className="info-icon">ℹ️</span>
              <h6>معلومات السند</h6>
            </div>
            <div className="panel-content">
              <div className="help-item">
                <strong>رقم السند:</strong>
                <p>{voucher.voucher_number}</p>
              </div>
              <div className="help-item">
                <strong>الحالة:</strong>
                <p>{t(`accounting.vouchers.voucherStatus.${voucher.status}`)}</p>
              </div>
              <div className="help-item">
                <strong>النوع:</strong>
                <p>{t(`accounting.vouchers.voucherTypes.${voucher.type}`)}</p>
              </div>
              <div className="help-item">
                <strong>التاريخ:</strong>
                <p>{voucher.voucher_date}</p>
              </div>
              <div className="help-item">
                <strong>المبلغ:</strong>
                <p>{voucher.amount} {voucher.currency || 'د.ك'}</p>
              </div>
            </div>
          </div>

          <div className="guide-panel">
            <div className="panel-header">
              <span className="info-icon">📋</span>
              <h6>إجراءات السند</h6>
            </div>
            <div className="panel-content">
              <div className="help-item">
                <strong>السندات المسودة:</strong>
                <p>يمكن التعديل والحذف والترحيل</p>
              </div>
              <div className="help-item">
                <strong>السندات المرحلة:</strong>
                <p>يمكن الإلغاء والطباعة فقط</p>
              </div>
              <div className="help-item">
                <strong>السندات الملغية:</strong>
                <p>يمكن الطباعة فقط</p>
              </div>
            </div>
          </div>
        </div>

        <div className="form-section">
          <div className="detail-section">
            <h3>معلومات السند</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">{t('accounting.vouchers.voucherDate')}:</span>
                <span className="detail-value">{voucher.voucher_date}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">{t('accounting.vouchers.type')}:</span>
                <span className="detail-value">{t(`accounting.vouchers.voucherTypes.${voucher.type}`)}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">{t('accounting.vouchers.amount')}:</span>
                <span className="detail-value amount">{voucher.amount} {voucher.currency || 'د.ك'}</span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>معلومات الحساب</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">{t('accounting.vouchers.account')}:</span>
                <span className="detail-value">{voucher.account?.account_code} - {voucher.account?.account_name}</span>
              </div>
            </div>
          </div>

          <div className="detail-section">
            <h3>معلومات الدفع</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">{t('accounting.vouchers.paymentMethod')}:</span>
                <span className="detail-value">{t(`accounting.vouchers.paymentMethods.${voucher.payment_method}`)}</span>
              </div>
              {voucher.reference_number && (
                <div className="detail-item">
                  <span className="detail-label">{t('accounting.vouchers.referenceNumber')}:</span>
                  <span className="detail-value">{voucher.reference_number}</span>
                </div>
              )}
              {voucher.check_number && (
                <div className="detail-item">
                  <span className="detail-label">{t('accounting.vouchers.checkNumber')}:</span>
                  <span className="detail-value">{voucher.check_number}</span>
                </div>
              )}
              {voucher.bank_name && (
                <div className="detail-item">
                  <span className="detail-label">{t('accounting.vouchers.bankName')}:</span>
                  <span className="detail-value">{voucher.bank_name}</span>
                </div>
              )}
            </div>
          </div>

          <div className="detail-section">
            <h3>الوصف والملاحظات</h3>
            <div className="detail-grid">
              <div className="detail-item full-width">
                <span className="detail-label">{t('accounting.vouchers.description')}:</span>
                <span className="detail-value">{voucher.description}</span>
              </div>
              {voucher.notes && (
                <div className="detail-item full-width">
                  <span className="detail-label">{t('accounting.vouchers.notes')}:</span>
                  <span className="detail-value">{voucher.notes}</span>
                </div>
              )}
            </div>
          </div>

          <div className="detail-section">
            <h3>معلومات إضافية</h3>
            <div className="detail-grid">
              <div className="detail-item">
                <span className="detail-label">تاريخ الإنشاء:</span>
                <span className="detail-value">{voucher.created_at}</span>
              </div>
              <div className="detail-item">
                <span className="detail-label">آخر تحديث:</span>
                <span className="detail-value">{voucher.updated_at}</span>
              </div>
              {voucher.created_by && (
                <div className="detail-item">
                  <span className="detail-label">أنشئ بواسطة:</span>
                  <span className="detail-value">{voucher.created_by.name}</span>
                </div>
              )}
            </div>
          </div>

          <div className="form-actions">
            {voucher.status === 'draft' && (
              <>
                <button 
                  type="button" 
                  className="btn-post"
                  onClick={handlePost}
                >
                  {t('accounting.vouchers.actions.post')}
                </button>
                <button 
                  type="button" 
                  className="btn-delete"
                  onClick={handleDelete}
                >
                  {t('accounting.vouchers.actions.delete')}
                </button>
              </>
            )}
            {voucher.status === 'posted' && (
              <button 
                type="button" 
                className="btn-cancel"
                onClick={handleCancel}
              >
                {t('accounting.vouchers.actions.cancel')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewVoucher;
